import { useLayoutEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { useIsPresent } from 'motion/react';
import { useIsFetching } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { moduleKeys, useRestoreModuleMutation, useUserModulesPageQuery } from '@entities/Module';
import { useAuthStore } from '@entities/User';
import { useModuleCreatorStore } from '@features/CreateModule';
import { paths } from '@shared/config';
import { cn } from '@shared/lib';
import { Button, toast } from '@shared/ui';
import { emptyModulesKind, splitAroundName } from '../model/emptyModules';
import './emptyModules.css';

interface EmptyModulesStateProps {
  /** Главная со строкой создания (ТЗ главной § 9): «Создать модуль» — как «+ Новый модуль» в чипе:
   *  модуль создаётся в модалке и выбирается в строке, ученик остаётся на главной. Без пропа —
   *  как раньше: после создания — в карточки модуля. */
  onCreateModule?: () => void;
  /** Просьба о почте уже в строке создания — здесь без своей (одна просьба на экране, ТЗ § 10). */
  emailInDock?: boolean;
  /** «Открыть удалённые» в «Моих модулях» на «Словах и модулях» — переключить фильтр на месте; без пропа — туда
   *  по адресу. */
  onOpenDeleted?: () => void;
}

/** Компонент показывают, только когда доступных модулей 0, — тогда и спрашиваем, есть ли удалённые (ТЗ демо 1.4). */
const DELETED_PROBE = { page: 1, size: 1, status: 'deleted' } as const;

/** Метка вместо названия модуля во фразе: на её месте — отдельный блок с обрезкой. */
const NAME_MARK = '\u0001';

/** Длинное название — не больше двух строк с многоточием, а короткое течёт во фразе, как обычный текст. */
const NAME_CLAMP: CSSProperties = { display: '-webkit-inline-box', WebkitBoxOrient: 'vertical', WebkitLineClamp: 2 };

/** Раскладка — по ширине самого блока, а не окна (emptyModules.css): на главной он бывает в двух третях колонки.
 *  Здесь — узкий вид: всё стопкой, кнопки во всю ширину. */
const BOX = 'em-box grid grid-cols-1 items-start gap-5 rounded-card border border-dashed border-line bg-card/50 px-5 py-6';
const ACTIONS = 'em-actions flex flex-shrink-0 flex-col items-stretch gap-2.5';

function Spinner() {
  return (
    <svg className="h-4 w-4 animate-spin motion-reduce:animate-none" viewBox="0 0 18 18" fill="none" aria-hidden>
      <circle cx="9" cy="9" r="7" stroke="currentColor" strokeOpacity="0.22" strokeWidth="2.2" />
      <path d="M16 9a7 7 0 0 0-7-7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" />
    </svg>
  );
}

/**
 * Модулей нет (главная, «Тренировка», «Мои модули» на «Словах и модулях»). Состояние — по порядку ТЗ демо 1.4: есть
 * удалённые — «вернуть» (один — «Восстановить модуль» на месте, несколько — «Открыть удалённые»); почта не
 * подтверждена — как раньше; иначе — «Создай свой первый модуль». Пока не ясно, есть ли удалённые, — скелетон той
 * же раскладки без кнопок.
 */
export function EmptyModulesState({ onCreateModule, emailInDock = false, onOpenDeleted }: EmptyModulesStateProps = {}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const openCreator = useModuleCreatorStore((s) => s.openCreator);
  const deletedQuery = useUserModulesPageQuery(DELETED_PROBE);
  const restore = useRestoreModuleMutation();
  const listsFetching = useIsFetching({ queryKey: moduleKeys.listsRoot });
  // Восстановленный модуль уходит из удалённых раньше, чем приходит список доступных: до конца перечитки списков
  // блок держит «Восстанавливаем…» — иначе мигнуло бы «Создай свой первый модуль», а потом появился модуль.
  const [restoring, setRestoring] = useState<{ id: string; title: string } | null>(null);
  const holding = restoring !== null && (restore.isPending || (restore.isSuccess && listsFetching > 0));
  // «Мои модули» меняют пустое состояние на список через AnimatePresence (mode="wait"): уходящий блок гаснет, а его
  // хуки живут — перечитка закончилась, и гаснущий блок успевал смениться на «Создай свой первый модуль»
  // (приёмка A2-02). Пока блок уходит, он показывает то, что было на экране в момент ухода.
  const present = useIsPresent();
  const frozen = useRef<ReactNode>(null);

  const deleted = deletedQuery.data;
  const first = deleted?.items[0] ?? null;
  const kind = holding
    ? 'restoreOne'
    : emptyModulesKind({
        deleted: deleted ? { total: deleted.total, first: first !== null } : deletedQuery.isPending ? 'loading' : null,
        emailVerified: !!user?.emailVerified,
      });

  const loadingView =
    kind === 'loading' ? (
      <div className="em-wrap">
        <div className={BOX} aria-busy="true">
          <div className="em-icon h-[80px] w-[80px] animate-skeleton rounded-[20px] bg-elev" />
          <div className="min-w-0">
            <div className="mb-2 h-4 w-28 animate-skeleton rounded bg-elev" />
            <div className="mb-2 h-7 w-3/5 animate-skeleton rounded bg-elev" />
            <div className="h-4 w-4/5 max-w-[480px] animate-skeleton rounded bg-elev" />
            <div className="mt-1.5 h-4 w-1/2 max-w-[320px] animate-skeleton rounded bg-elev" />
          </div>
          <div className="em-actions em-skel-btn h-[52px] w-full animate-skeleton rounded-pill bg-elev" />
        </div>
      </div>
    ) : null;

  const onRestore = () => {
    if (holding || !first) return;
    const module = { id: first.id, title: first.title };
    setRestoring(module);
    restore.mutate(module.id, {
      onSuccess: () =>
        toast({
          tone: 'success',
          title: t('moduleDetail.restore.toastTitle'),
          message: t('moduleDetail.restore.toastBody', { name: module.title }),
        }),
      onError: () => toast({ tone: 'error', message: t('moduleDetail.restore.error') }),
    });
  };

  let kicker: string;
  let title: string;
  let body: ReactNode;
  let actions: ReactNode = null;
  switch (kind) {
    case 'restoreOne': {
      const named = splitAroundName(t('emptyModules.restore.body', { name: NAME_MARK }), NAME_MARK, (holding ? restoring : first)?.title ?? '');
      kicker = t('moduleDetail.restore.kicker');
      title = t('moduleDetail.restore.title');
      body = (
        <>
          {named.before}
          <span className="max-w-full overflow-hidden align-top" style={NAME_CLAMP}>
            {named.name}
          </span>
          {named.after}
        </>
      );
      actions = (
        <Button
          size="lg"
          onClick={onRestore}
          // Не disabled: кнопка, на которую нажали, держит фокус клавиатуры; повтор заблокирован в onRestore.
          aria-disabled={holding || undefined}
          aria-busy={holding || undefined}
          className={cn('w-full', holding && 'cursor-wait')}
        >
          {/* Обе подписи в одной ячейке: ширина — по длинной, «Восстанавливаем…» кнопку не сужает. */}
          <span className="grid">
            <span className={cn('col-start-1 row-start-1 text-center', holding && 'invisible')}>{t('moduleDetail.restore.cta')}</span>
            <span className={cn('col-start-1 row-start-1 inline-flex items-center justify-center gap-2', !holding && 'invisible')}>
              <Spinner />
              {t('emptyModules.restore.busy')}
            </span>
          </span>
        </Button>
      );
      break;
    }
    case 'restoreMany':
      kicker = t('emptyModules.restore.kickerMany');
      title = t('emptyModules.restore.titleMany');
      body = t('emptyModules.restore.bodyMany');
      actions = (
        <Button size="lg" onClick={onOpenDeleted ?? (() => navigate(paths.wordsDeleted))} className="w-full">
          {t('emptyModules.restore.ctaMany')}
        </Button>
      );
      break;
    case 'locked':
      kicker = t('emptyModules.locked.kicker');
      title = t(emailInDock ? 'emptyModules.locked.titleDock' : 'emptyModules.locked.title');
      body = t('emptyModules.locked.body');
      actions = (
        <Button size="lg" onClick={() => navigate(paths.wordsReady)} className="w-full">
          {t('emptyModules.locked.cta')}
        </Button>
      );
      break;
    default:
      kicker = t('emptyModules.full.kicker');
      title = t('emptyModules.full.title');
      body = t('emptyModules.full.body');
      actions = (
        <>
          <Button variant="ghost" size="lg" onClick={() => navigate(paths.wordsReady)} className="w-full">
            {t('emptyModules.full.secondary')}
          </Button>
          <Button size="lg" onClick={onCreateModule ?? openCreator} className="w-full">
            {t('emptyModules.full.cta')}
          </Button>
        </>
      );
  }

  const view = loadingView ?? (
    <div className="em-wrap">
      <div className={BOX}>
        <div className="em-icon inline-flex h-[80px] w-[80px] items-center justify-center rounded-[20px] bg-elev font-serif text-[36px] text-muted">
          ∅
        </div>

        <div className="min-w-0">
          <div className="mb-2 inline-flex items-center gap-2 text-[12px] font-semibold uppercase tracking-[0.16em] text-accent">
            {kicker}
          </div>
          <div className="mb-1.5 font-serif text-[22px] font-medium leading-[1.25] tracking-[-0.01em] text-fg">{title}</div>
          <div className="max-w-[640px] text-[14px] leading-[1.55] text-muted [overflow-wrap:anywhere]">{body}</div>
        </div>

        {actions && <div className={ACTIONS}>{actions}</div>}
      </div>
    </div>
  );
  useLayoutEffect(() => {
    if (present) frozen.current = view;
  });
  return present || frozen.current === null ? view : frozen.current;
}
